import { useState, useEffect, useCallback } from "react"

import { Guid } from "@/types"
import AssetService from "services/AssetService"
import Reservation from "models/Reservation"

export default function useReservation({
    reservationId,
    withPricingDetails,
    hydrateConfiguration,
    hydrateInvoicePositions,
} : {
    reservationId?: Guid | null,
    withPricingDetails?: boolean
    hydrateConfiguration?: boolean
    hydrateInvoicePositions?: boolean
}, onFail?: () => void) {
    const [reservation, setReservation] = useState<Reservation | null>(null)
    const [isLoading, setIsLoading] = useState(false)

    const fetchReservation = useCallback(async (id: Guid) => {
        setIsLoading(true)
        setReservation(null)

        try {
            const fetchedReservation = await AssetService.getReservation({
                reservationId: id,
                withPricingDetails,
                hydrateConfiguration,
                hydrateInvoicePositions,
            })

            setReservation(fetchedReservation)
            setIsLoading(false)
            return fetchedReservation
        } catch(e) {
            setReservation(null)
            if (typeof onFail === "function") {
                onFail()
            }
        }

        setIsLoading(false)
    }, [hydrateConfiguration, hydrateInvoicePositions, onFail, withPricingDetails])

    const refetchReservation = useCallback(async () => {
        if (!reservationId) {
            setReservation(null)
            setIsLoading(false)
            return
        }
        return fetchReservation(reservationId)
    }, [fetchReservation, reservationId])

    useEffect(() => {
        refetchReservation()
    }, [refetchReservation])

    return {
        reservation,
        refetchReservation,
        isLoading
    }
}
